import React from 'react';

import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {massUnitTypePredicate, molUnitTypePredicate} from 'inventory/InventoryUnitUtil';
import {
  CHEMISTRY_REACTION_ROLE_TO_DISPLAY_NAME,
  ChemistryReactionRole,
} from 'notebook/integrations/chemistry/ChemistryStructuredTableConstants';
import {CustomColumnDataType} from 'notebook/structured-tables/custom-columns/CustomColumnConstants';
import GenericStructuredTableAdapter from 'notebook/structured-tables/GenericStructuredTableAdapter';
import {
  SchemaAndColumnGroup,
  StructuredTable,
  StructuredTableRow,
  StructuredTableColumn,
  StructuredTableCustomComponentSlotProps,
  StructuredTableColumnGroups,
  CustomStructuredTableColumn,
} from 'notebook/structured-tables/StructuredTableConstants';
import {makeStructuredTableColumn} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import {StructuredTableInsertionContextValue} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import {
  RowMetadata,
  TABLE_MINI_COLUMN_WIDTH,
  TABLE_SMALL_COLUMN_WIDTH,
  CellIndex,
  CustomEval,
  TableColumnType,
} from 'notebook/tables/TableConstants';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';

import {getMolecularFormulaColumnCustomEval} from './custom-evals/MolecularFormulaCustomEval';
import {getMolecularWeightColumnCustomEval} from './custom-evals/MolecularWeightCustomEval';
import {getDefaultUnitCustomEval} from './custom-evals/StoichiometryUnitDefaultsCustomEval';
import StoichiometryTableEmbeddedChemicalSketcher from './StoichiometryTableEmbeddedChemicalSketcher';

export interface StoichiometryStructuredTableRowMetadata extends RowMetadata {}

export interface StoichiometryStructuredTableMetadata {}

export type StoichiometryStructuredTable = StructuredTable<
  StoichiometryStructuredTableMetadata,
  StoichiometryStructuredTableRowMetadata
>;

const REACTION_ROLE_OPTIONS = Object.values(ChemistryReactionRole).map(
  (role) => CHEMISTRY_REACTION_ROLE_TO_DISPLAY_NAME[role]
);

const DEFAULT_MOL_UNIT_SYMBOL = 'mol';
const DEFAULT_MASS_UNIT_SYMBOL = 'g';

export default class StoichiometryStructuredTableAdapter extends GenericStructuredTableAdapter<
  StoichiometryStructuredTableMetadata,
  StoichiometryStructuredTableRowMetadata
> {
  getBlankRowMetadata(
    tableMetadata: StoichiometryStructuredTableMetadata
  ): StoichiometryStructuredTableRowMetadata {
    return {};
  }

  override getDisplayName(structuredTable: StoichiometryStructuredTable): string {
    return 'Reaction';
  }

  override allowSingleInputs(_: StoichiometryStructuredTable): boolean {
    return false;
  }

  override injectFrontEndMetadata({
    column,
    schemas,
    tableMetadata,
  }: {
    column: StructuredTableColumn;
    schemas: Array<SchemaAndColumnGroup>;
    tableMetadata: StoichiometryStructuredTableMetadata;
  }): StructuredTableColumn {
    switch (column.columnGroup) {
      case StructuredTableColumnGroups.CHEMISTRY_MOLECULE: {
        return {
          ...column,
          frontEndMetadata: {
            validSchemas: 'ALL',
          },
        };
      }
      case StructuredTableColumnGroups.CHEMISTRY_REACTION_ROLE: {
        assert(
          column.columnType === TableColumnType.OPTION_SELECTOR,
          'Reaction Role column must be an OPTION_SELECTOR'
        );
        return {
          ...column,
          options: REACTION_ROLE_OPTIONS,
        };
      }
      case StructuredTableColumnGroups.CHEMISTRY_AMOUNT_UNIT: {
        return {
          ...column,
          frontEndMetadata: {
            unitTypesFilterPredicate: molUnitTypePredicate,
          },
        };
      }
      case StructuredTableColumnGroups.CHEMISTRY_MASS_UNIT: {
        return {
          ...column,
          frontEndMetadata: {
            unitTypesFilterPredicate: massUnitTypePredicate,
          },
        };
      }
      default:
        return column;
    }
  }

  override _getAllSupportedColumns({
    entryId,
    metadata,
    schemas,
    selectorsById,
    structuredTableInsertionContext,
  }: {
    entryId: string | null;
    metadata: StoichiometryStructuredTableMetadata;
    schemas: Array<SchemaAndColumnGroup>;
    selectorsById: {[selectorId: string]: SchemaFieldSelector};
    structuredTableInsertionContext: StructuredTableInsertionContextValue;
  }): Array<StructuredTableColumn> {
    return [
      {
        ...makeStructuredTableColumn({
          columnType: TableColumnType.EXISTING_BIOENTITY,
          name: 'Compound',
          columnGroup: StructuredTableColumnGroups.CHEMISTRY_MOLECULE,
        }),
        columnType: TableColumnType.EXISTING_BIOENTITY,
        bioentityTypeFilters: [ModelTypeConstants.MOLECULE],
      },
      {
        ...makeStructuredTableColumn({columnType: TableColumnType.OPTION_SELECTOR, name: 'Role'}),
        columnType: TableColumnType.OPTION_SELECTOR,
        options: REACTION_ROLE_OPTIONS,
        columnGroup: StructuredTableColumnGroups.CHEMISTRY_REACTION_ROLE,
        width: TABLE_SMALL_COLUMN_WIDTH,
      },
      {
        ...makeStructuredTableColumn({columnType: TableColumnType.CUSTOM, name: 'LR'}),
        columnType: TableColumnType.CUSTOM,
        customColumnConfig: {dataType: CustomColumnDataType.BOOLEAN},
        width: TABLE_MINI_COLUMN_WIDTH,
      },
      makeDecimalStructuredTableColumn({name: 'Equiv'}),
      makeDecimalStructuredTableColumn({name: 'MW'}),
      makeDecimalStructuredTableColumn({name: 'Mol (plan)'}),
      makeDecimalStructuredTableColumn({name: 'Mols'}),
      {
        ...makeStructuredTableColumn({
          columnType: TableColumnType.UNIT_REFERENCE,
          name: 'Mol Unit',
        }),
        columnType: TableColumnType.UNIT_REFERENCE,
        columnGroup: StructuredTableColumnGroups.CHEMISTRY_AMOUNT_UNIT,
      },
      makeDecimalStructuredTableColumn({name: 'Amt (plan)'}),
      {
        ...makeStructuredTableColumn({
          columnType: TableColumnType.UNIT_REFERENCE,
          name: 'Amt Unit',
        }),
        columnType: TableColumnType.UNIT_REFERENCE,
        columnGroup: StructuredTableColumnGroups.CHEMISTRY_MASS_UNIT,
      },
      makeStructuredTableColumn({columnType: TableColumnType.STRING, name: 'Formula', isReadOnly: true}),
    ];
  }

  /**
   * Returns the column index of the Name column (EXISTING_BIOENTITY) that contains the molecule.
   */
  private getColumnIndexesOfMoleculeDependencies = (columns: Array<StructuredTableColumn>): Array<number> => [
    columns.findIndex(({columnType}) => columnType === TableColumnType.EXISTING_BIOENTITY),
  ];

  /**
   * Returns the column indexes of dependencies for columns that need to read from other columns.
   */
  private getColumnDependencyIndexes(
    column: StructuredTableColumn,
    columns: Array<StructuredTableColumn>
  ): Array<number> {
    // Molecular Weight and Molecular Formula columns depend on the Name column (EXISTING_BIOENTITY)
    if (
      column.columnType === TableColumnType.STRING ||
      (column.columnType === TableColumnType.CUSTOM && (column.name === 'MW' || column.name === 'Formula'))
    ) {
      return this.getColumnIndexesOfMoleculeDependencies(columns);
    }
    return [];
  }

  override getTableSpecificCustomEvalArgs(
    columns: Array<StructuredTableColumn>,
    currentColumnIndex: number,
    currentRowIndex: number,
    tableMetadata: StoichiometryStructuredTableMetadata,
    toSpreadsheetIndex: (cellId: CellIndex) => string
  ): Array<string> {
    const column = columns[currentColumnIndex];
    const dependencyColumnIndexes = this.getColumnDependencyIndexes(column, columns);

    return dependencyColumnIndexes.map((dependencyIndex, slotIndex) => {
      assert(
        dependencyIndex !== -1,
        `Could not find dependency at slot ${slotIndex} for column at index ${currentColumnIndex}`
      );
      return `=${toSpreadsheetIndex({row: currentRowIndex, column: dependencyIndex})}`;
    });
  }

  override getTableSpecificCustomEvalFn(
    column: StructuredTableColumn,
    row: StructuredTableRow<StoichiometryStructuredTableRowMetadata>,
    metadata: StoichiometryStructuredTableMetadata
  ): CustomEval<{
    tableMetadata: StoichiometryStructuredTableMetadata;
    rowMetadata: StoichiometryStructuredTableRowMetadata;
  }> | null {
    if (column.columnType === TableColumnType.STRING && column.name === 'Formula') {
      return getMolecularFormulaColumnCustomEval;
    }
    if (column.columnType === TableColumnType.CUSTOM && column.name === 'MW') {
      return getMolecularWeightColumnCustomEval;
    }
    if (column.columnType === TableColumnType.UNIT_REFERENCE) {
      if (column.columnGroup === StructuredTableColumnGroups.CHEMISTRY_AMOUNT_UNIT) {
        return getDefaultUnitCustomEval(DEFAULT_MOL_UNIT_SYMBOL);
      }
      if (column.columnGroup === StructuredTableColumnGroups.CHEMISTRY_MASS_UNIT) {
        return getDefaultUnitCustomEval(DEFAULT_MASS_UNIT_SYMBOL);
      }
    }
    return null;
  }

  override isPositionReadOnly(
    currentColumnIndex: number,
    row: StructuredTableRow<StoichiometryStructuredTableRowMetadata>,
    structuredTable: StoichiometryStructuredTable
  ): boolean {
    return super.isPositionReadOnly(currentColumnIndex, row, structuredTable);
  }

  override experimental_renderAboveTableCustomComponent(
    props: StructuredTableCustomComponentSlotProps<
      StoichiometryStructuredTableMetadata,
      StoichiometryStructuredTableRowMetadata
    >
  ): React.ReactNode {
    return <StoichiometryTableEmbeddedChemicalSketcher {...props} />;
  }
}

/**
 * ⚠️ CHEMISTRY_TABLE: There is a need to create more general column types that can be used for purely
 * typed input. This may be Custom Columns, but it may be something else (schema-less schema fields)
 *
 * Helper to make a decimal column
 */
function makeDecimalStructuredTableColumn(
  overrides: Partial<CustomStructuredTableColumn> = {}
): CustomStructuredTableColumn {
  return {
    ...makeStructuredTableColumn({columnType: TableColumnType.CUSTOM, name: 'Custom Column'}),
    columnType: TableColumnType.CUSTOM,
    customColumnConfig: {dataType: CustomColumnDataType.FLOAT},
    ...overrides,
  };
}
